import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, Button, Form } from 'react-bootstrap';
import { userService } from '../../_services/user.service';
import { authenticatedIdentity, readSession, recoverSession } from '../../_services/webSession';
import { readAccounts } from '../../_services/savedAccounts';
import AccountPicker from '../../_components/AccountPicker';
import AccountLogoutFlow from '../../_components/AccountLogoutFlow';
import logo from '../../Assets/dataco_logo-NEW.png';

export function LoginPage({ recoveryReason }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const passwordInput = useRef(null);
    const inFlight = useRef(false);
    const selectionStarted = useRef(null);
    const action = location.state?.accountAction;
    const session = readSession();
    const explicitAction = session && ['logout', 'switch'].includes(action?.type)
        && action.originIdentity === authenticatedIdentity();
    const from = location.state?.from?.pathname || '/';

    useEffect(() => {
        if (readSession() && !explicitAction) {
            navigate('/', { replace: true });
        } else if (!readSession() && localStorage.getItem('user')) {
            recoverSession(authenticatedIdentity(), 'Your session expired. Please sign in again.');
        }
    }, [explicitAction, location.key, navigate]);

    async function selectAccount(account) {
        if (inFlight.current) return;

        setUsername(account.username);
        setPassword('');
        setError('');
        if (account.mode !== 'one_click') {
            passwordInput.current?.focus();
            return;
        }

        inFlight.current = true;
        setLoading(true);
        try {
            await userService.loginRemembered(account);
            navigate('/', { replace: true });
        } catch (failure) {
            setError(failure.message || 'Saved login failed. Please retry.');
            passwordInput.current?.focus();
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }

    useEffect(() => {
        const selected = location.state?.selectedAccount;
        if (!selected || readSession() || selectionStarted.current === location.key) return;

        selectionStarted.current = location.key;
        const account = readAccounts().find(entry => entry.id === String(selected.id))
            || { ...selected, mode: 'password', selector: null };
        // Keep a logout notice visible while the selected account signs in or asks for a password.
        const notice = location.state?.notice;
        navigate('/login', { replace: true, state: notice ? { notice } : null });
        selectAccount(account);
    }, [location.key, navigate]);

    useEffect(() => {
        if (!loading && error && username) passwordInput.current?.focus();
    }, [loading, error, username]);

    async function submit(event) {
        event.preventDefault();
        if (inFlight.current || !username || !password) return;

        inFlight.current = true;
        setLoading(true);
        setError('');
        try {
            await userService.login(username, password);
            setPassword('');
            navigate(from, { replace: true });
        } catch (failure) {
            setError(failure.message || 'Login failed. Please retry.');
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }

    if (explicitAction) {
        return <AccountLogoutFlow action={action} onCancel={() => navigate(action.returnTo || '/', { replace: true, state: null })} onComplete={notice => {
            const state = {
                ...(action.type === 'switch' ? { selectedAccount: action.targetAccount } : {}),
                ...(notice ? { notice } : {}),
            };
            navigate('/login', { replace: true, state: Object.keys(state).length ? state : null });
        }} />;
    }

    return <div className="login-page" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        padding: 20,
    }}>
        <div style={{
            width: '100%',
            maxWidth: 420,
            padding: 30,
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 25 }}>
                <img src={logo} alt="Dataco" style={{ maxWidth: 200 }} />
            </div>
            {(location.state?.notice || recoveryReason) && <Alert variant="info">{location.state?.notice || recoveryReason}</Alert>}
            <Form onSubmit={submit}>
                <Form.Group className="mb-3" controlId="login-username">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                        name="username"
                        autoComplete="username"
                        required
                        value={username}
                        disabled={loading}
                        onChange={event => setUsername(event.target.value)}
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="login-password">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                        ref={passwordInput}
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        disabled={loading}
                        onChange={event => setPassword(event.target.value)}
                    />
                </Form.Group>
                {error && <Alert variant="danger" role="alert">{error}</Alert>}
                <Button type="submit" className="w-100" disabled={loading}>{loading ? 'Signing in…' : 'Login'}</Button>
                {loading && <p role="status" className="account-picker-note mt-2">Signing in to your account…</p>}
            </Form>
            <AccountPicker onSelect={selectAccount} disabled={loading} />
        </div>
    </div>;
}
